import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { aapneAdmin, kanAapneAdmin } from '../lib/aapneAdmin.ts';

/** Kontoen som er innlogget (samme nøkkel overalt, så TanStack Query deduper kallet). */
export function useKonto() {
  return useQuery({ queryKey: ['konto-me'], queryFn: () => api.kontoMe(), retry: false });
}

/**
 * Alltid synlig innloggings-knapp (øverst til høyre). Viser «Logg inn» når man
 * ikke er innlogget, og «Min side» når man er det — med «Admin» til høyre for den
 * når kontoen er superadmin eller klubbadmin (på mobil ligger Admin i menyen).
 */
export function AuthButton() {
  const me = useKonto();
  const loggedIn = me.isSuccess && !!me.data;
  return (
    <>
      <Link
        to={loggedIn ? '/min' : '/logg-inn'}
        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white shadow-sm"
      >
        {loggedIn ? '⛵ Min side' : 'Logg inn'}
      </Link>
      {loggedIn && kanAapneAdmin(me.data) && <AdminKnapp className="hidden sm:inline-flex" />}
    </>
  );
}

/**
 * Åpner admin via kontoen. Står man allerede i admin, er knappen markert og går til
 * oversikten. Feiler broen, sendes man til /admin/login, som forklarer hvorfor.
 */
export function AdminKnapp({ className = '', onClick }: { className?: string; onClick?: () => void }) {
  const loc = useLocation();
  const [busy, setBusy] = useState(false);
  const iAdmin = loc.pathname.startsWith('/admin') && loc.pathname !== '/admin/login';
  const stil = `items-center text-sm font-semibold px-3 py-1.5 rounded-lg border ${
    iAdmin
      ? 'bg-slate-800 border-sky-500 text-sky-300'
      : 'bg-slate-800 border-slate-700 text-slate-100 hover:bg-slate-700'
  } ${className}`;

  if (iAdmin) {
    return (
      <Link to="/admin" onClick={onClick} className={stil}>
        Admin
      </Link>
    );
  }

  async function aapne() {
    onClick?.();
    setBusy(true);
    try {
      await aapneAdmin();
    } catch {
      window.location.href = '/admin/login';
    }
  }

  return (
    <button type="button" onClick={aapne} disabled={busy} className={`${stil} disabled:opacity-60`}>
      {busy ? 'Åpner…' : 'Admin'}
    </button>
  );
}
